import { Badge } from "@/components/ui/badge";
import type { ConnectorFileChange } from "@/lib/api";
import { changesSecret, diffHunks, diffLineTokens, formatBytes, lineDiff, secretLines, type DiffHunk, type DiffLine } from "@/lib/connectors";
import type { Token } from "@/lib/highlight";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight, FileQuestion, KeyRound } from "lucide-react";
import { useMemo, useState } from "react";
import { CodeTokens, SecretBadge } from "./parts";

type Vars = { name: string; secret?: boolean }[];

// Files changed in one version against the version before it. Lines that read
// a secret variable are marked, each side by its own version's declarations,
// and so is any file with such a change, so a reviewer sees where the key goes
// before anything else.
export function ConnectorDiff({ files, before, after }: { files: ConnectorFileChange[]; before: Vars; after: Vars }) {
  const changed = files.filter((f) => f.status !== "unchanged");
  if (changed.length === 0) {
    return <p className="rounded-lg border p-4 text-sm text-muted-foreground">No file changes.</p>;
  }
  // Secret-touching files first, then by path.
  const sorted = changed.sort(
    (a, b) => Number(!!b.touchesSecret) - Number(!!a.touchesSecret) || a.path.localeCompare(b.path),
  );
  return (
    <div className="flex flex-col gap-3">
      {sorted.map((f) =>
        f.kind === "binary" || f.kind === "too_large" ? (
          <OpaqueFileRow key={f.path} file={f} />
        ) : (
          <FileDiff key={f.path} file={f} beforeVars={before} afterVars={after} />
        ),
      )}
    </div>
  );
}

function FileDiff({ file, beforeVars, afterVars }: { file: ConnectorFileChange; beforeVars: Vars; afterVars: Vars }) {
  const [open, setOpen] = useState(true);
  const lines = useMemo(() => lineDiff(file.before ?? null, file.after ?? null), [file.before, file.after]);
  const hunks = useMemo(() => diffHunks(lines), [lines]);
  const tokensOf = useMemo(
    () => diffLineTokens(file.path, file.before ?? null, file.after ?? null),
    [file.path, file.before, file.after],
  );
  const isSecret = useMemo(() => {
    const before = secretLines(file.before, beforeVars);
    const after = secretLines(file.after, afterVars);
    return (l: DiffLine) => changesSecret(l, before, after);
  }, [file.before, file.after, beforeVars, afterVars]);
  const added = lines.filter((l) => l.kind === "add").length;
  const removed = lines.filter((l) => l.kind === "del").length;
  const secret = file.touchesSecret || lines.some(isSecret);

  return (
    <div className={cn("overflow-hidden rounded-lg border", secret && "border-amber-500/60")}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 bg-muted/40 px-3 py-2 text-left hover:bg-muted/70"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="size-4 shrink-0" /> : <ChevronRight className="size-4 shrink-0" />}
        <code className="min-w-0 flex-1 truncate font-mono text-xs">{file.path}</code>
        {secret && <SecretBadge>Uses a secret</SecretBadge>}
        {file.status !== "modified" && (
          <Badge variant="secondary">{file.status === "added" ? "New file" : "Deleted"}</Badge>
        )}
        <span className="shrink-0 font-mono text-xs tabular-nums">
          <span className="text-emerald-700 dark:text-emerald-400">+{added}</span>{" "}
          <span className="text-red-700 dark:text-red-400">−{removed}</span>
        </span>
      </button>
      {open && (
        <div className="overflow-x-auto border-t font-mono text-xs leading-5">
          <table className="w-full border-collapse">
            <tbody>
              {hunks.map((h, hi) => (
                <HunkRows key={hi} hunk={h} isSecret={isSecret} tokensOf={tokensOf} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// A binary or very large file: no text to diff, only what happened to it.
function OpaqueFileRow({ file }: { file: ConnectorFileChange }) {
  const what = file.kind === "binary" ? "image or other non-text file" : "too large to show";
  const change = file.status === "added" ? "added" : file.status === "removed" ? "removed" : "changed";
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border bg-muted/40 px-3 py-2 text-xs">
      <FileQuestion className="size-4 shrink-0 text-muted-foreground" />
      <code className="min-w-0 truncate font-mono">{file.path}</code>
      <span className="text-muted-foreground">
        · {what} · {change}
        {file.size != null ? ` · ${formatBytes(file.size)}` : ""}
      </span>
    </div>
  );
}

function HunkRows({
  hunk,
  isSecret,
  tokensOf,
}: {
  hunk: DiffHunk;
  isSecret: (line: DiffLine) => boolean;
  tokensOf: (line: DiffLine) => Token[];
}) {
  return (
    <>
      {hunk.hiddenBefore > 0 && <FoldedRow count={hunk.hiddenBefore} />}
      {hunk.lines.map((l, i) => {
        const secret = isSecret(l);
        return (
          <tr
            key={i}
            className={cn(l.kind === "add" && "bg-emerald-500/10", l.kind === "del" && "bg-red-500/10")}
          >
            <td
              className={cn(
                "w-10 select-none px-2 text-right text-muted-foreground/70 tabular-nums",
                secret && "border-l-[3px] border-l-amber-500 bg-amber-400/25 dark:bg-amber-400/20",
              )}
            >
              {l.oldNo ?? ""}
            </td>
            <td className="w-10 select-none px-2 text-right text-muted-foreground/70 tabular-nums">{l.newNo ?? ""}</td>
            <td className="w-9 select-none whitespace-nowrap text-center text-muted-foreground">
              {secret && <KeyRound aria-label="Uses a secret" className="mr-1 inline size-3 text-amber-700 dark:text-amber-400" />}
              {l.kind === "add" ? "+" : l.kind === "del" ? "−" : ""}
            </td>
            <td className="whitespace-pre pr-4">
              <CodeTokens tokens={tokensOf(l)} />
            </td>
          </tr>
        );
      })}
      {(hunk.hiddenAfter ?? 0) > 0 && <FoldedRow count={hunk.hiddenAfter!} />}
    </>
  );
}

function FoldedRow({ count }: { count: number }) {
  return (
    <tr className="bg-muted/30 text-muted-foreground">
      <td colSpan={4} className="px-3 py-0.5 select-none">
        ⋯ {count} unchanged {count === 1 ? "line" : "lines"}
      </td>
    </tr>
  );
}
